/**
 * Permissions Configuration
 * Defines all permissions and role-based access control for BE Study System
 */

import { USER_ROLES } from './beStudyMockData';

// ============================================
// PERMISSION DEFINITIONS
// ============================================

export const PERMISSIONS = {
  // Template Permissions
  TEMPLATE_VIEW: 'template:view',
  TEMPLATE_CREATE: 'template:create',
  TEMPLATE_EDIT: 'template:edit',
  TEMPLATE_DELETE: 'template:delete',
  TEMPLATE_UPLOAD: 'template:upload',
  
  // Document Permissions
  DOCUMENT_VIEW_OWN: 'document:view:own',
  DOCUMENT_VIEW_ALL: 'document:view:all',
  DOCUMENT_VIEW_ASSIGNED: 'document:view:assigned',
  DOCUMENT_CREATE: 'document:create',
  DOCUMENT_EDIT: 'document:edit',
  DOCUMENT_DELETE: 'document:delete',
  DOCUMENT_SUBMIT_REVIEW: 'document:submit:review',
  DOCUMENT_APPROVE: 'document:approve',
  DOCUMENT_REJECT: 'document:reject',
  
  // Section Permissions
  SECTION_EDIT: 'section:edit',
  SECTION_COMPLETE: 'section:complete',
  SECTION_UPLOAD: 'section:upload',
  SECTION_GENERATE: 'section:generate',
  
  // Comment Permissions
  COMMENT_VIEW: 'comment:view',
  COMMENT_ADD: 'comment:add',
  COMMENT_EDIT_OWN: 'comment:edit:own',
  COMMENT_DELETE_OWN: 'comment:delete:own',
  COMMENT_RESOLVE: 'comment:resolve',
  
  // User Management Permissions
  USER_VIEW: 'user:view',
  USER_CREATE: 'user:create',
  USER_EDIT: 'user:edit',
  USER_DELETE: 'user:delete',
  USER_CHANGE_ROLE: 'user:change:role',
  USER_CHANGE_STATUS: 'user:change:status',
  
  // Prompt Management Permissions
  PROMPT_VIEW: 'prompt:view',
  PROMPT_CREATE: 'prompt:create',
  PROMPT_EDIT: 'prompt:edit',
  PROMPT_DELETE: 'prompt:delete',
  
  // Settings Permissions
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',
  
  // Dashboard Permissions
  DASHBOARD_ADMIN: 'dashboard:admin',
  DASHBOARD_USER: 'dashboard:user',
  DASHBOARD_REVIEWER: 'dashboard:reviewer',
  
  // System Permissions
  SYSTEM_CONFIG: 'system:config',
  SYSTEM_AUDIT: 'system:audit',
};

// ============================================
// ROLE-PERMISSION MAPPINGS
// ============================================

export const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: [
    // All Template Permissions
    PERMISSIONS.TEMPLATE_VIEW,
    PERMISSIONS.TEMPLATE_CREATE,
    PERMISSIONS.TEMPLATE_EDIT,
    PERMISSIONS.TEMPLATE_DELETE,
    PERMISSIONS.TEMPLATE_UPLOAD,
    
    // All Document Permissions
    PERMISSIONS.DOCUMENT_VIEW_OWN,
    PERMISSIONS.DOCUMENT_VIEW_ALL,
    PERMISSIONS.DOCUMENT_VIEW_ASSIGNED,
    PERMISSIONS.DOCUMENT_CREATE,
    PERMISSIONS.DOCUMENT_EDIT,
    PERMISSIONS.DOCUMENT_DELETE,
    PERMISSIONS.DOCUMENT_SUBMIT_REVIEW,
    PERMISSIONS.DOCUMENT_APPROVE,
    PERMISSIONS.DOCUMENT_REJECT,
    
    // All Section Permissions
    PERMISSIONS.SECTION_EDIT,
    PERMISSIONS.SECTION_COMPLETE,
    PERMISSIONS.SECTION_UPLOAD,
    PERMISSIONS.SECTION_GENERATE,
    
    // All Comment Permissions
    PERMISSIONS.COMMENT_VIEW,
    PERMISSIONS.COMMENT_ADD,
    PERMISSIONS.COMMENT_EDIT_OWN,
    PERMISSIONS.COMMENT_DELETE_OWN,
    PERMISSIONS.COMMENT_RESOLVE,
    
    // All User Management Permissions
    PERMISSIONS.USER_VIEW,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_EDIT,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.USER_CHANGE_ROLE,
    PERMISSIONS.USER_CHANGE_STATUS,
    
    // All Prompt Permissions
    PERMISSIONS.PROMPT_VIEW,
    PERMISSIONS.PROMPT_CREATE,
    PERMISSIONS.PROMPT_EDIT,
    PERMISSIONS.PROMPT_DELETE,
    
    // All Settings Permissions
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    
    // Admin Dashboard
    PERMISSIONS.DASHBOARD_ADMIN,
    
    // System Permissions
    PERMISSIONS.SYSTEM_CONFIG,
    PERMISSIONS.SYSTEM_AUDIT,
  ],
  
  [USER_ROLES.USER]: [
    // Limited Template Permissions (View only for selection)
    PERMISSIONS.TEMPLATE_VIEW,
    
    // Document Permissions (Own documents)
    PERMISSIONS.DOCUMENT_VIEW_OWN,
    PERMISSIONS.DOCUMENT_CREATE,
    PERMISSIONS.DOCUMENT_EDIT,
    PERMISSIONS.DOCUMENT_SUBMIT_REVIEW,
    
    // Section Permissions
    PERMISSIONS.SECTION_EDIT,
    PERMISSIONS.SECTION_COMPLETE,
    PERMISSIONS.SECTION_UPLOAD,
    PERMISSIONS.SECTION_GENERATE,
    
    // Comment Permissions
    PERMISSIONS.COMMENT_VIEW,
    PERMISSIONS.COMMENT_ADD,
    PERMISSIONS.COMMENT_EDIT_OWN,
    PERMISSIONS.COMMENT_DELETE_OWN,
    
    // User Dashboard
    PERMISSIONS.DASHBOARD_USER,
    
    // Personal Settings
    PERMISSIONS.SETTINGS_VIEW,
  ],
  
  [USER_ROLES.REVIEWER]: [
    // Template View (for reference)
    PERMISSIONS.TEMPLATE_VIEW,
    
    // Document Permissions (Assigned documents)
    PERMISSIONS.DOCUMENT_VIEW_ASSIGNED,
    PERMISSIONS.DOCUMENT_APPROVE,
    PERMISSIONS.DOCUMENT_REJECT,
    
    // Comment Permissions (Full)
    PERMISSIONS.COMMENT_VIEW,
    PERMISSIONS.COMMENT_ADD,
    PERMISSIONS.COMMENT_EDIT_OWN,
    PERMISSIONS.COMMENT_DELETE_OWN,
    PERMISSIONS.COMMENT_RESOLVE,
    
    // Reviewer Dashboard
    PERMISSIONS.DASHBOARD_REVIEWER,
    
    // Personal Settings
    PERMISSIONS.SETTINGS_VIEW,
  ],
};

// ============================================
// DOCUMENT EDITOR ACTIONS BY ROLE
// ============================================

export const EDITOR_ACTIONS = {
  EDIT_TEXT: 'edit_text',
  SUGGEST_CHANGES: 'suggest_changes',
  ADD_COMMENT: 'add_comment',
  MARK_SECTION_COMPLETE: 'mark_section_complete',
  MARK_DOCUMENT_COMPLETE: 'mark_document_complete',
  REQUEST_REVIEW: 'request_review',
  APPROVE: 'approve',
  REJECT: 'reject',
  VIEW_HISTORY: 'view_history',
  DOWNLOAD: 'download',
};

export const EDITOR_ACTION_PERMISSIONS = {
  [USER_ROLES.ADMIN]: [
    EDITOR_ACTIONS.EDIT_TEXT,
    EDITOR_ACTIONS.SUGGEST_CHANGES,
    EDITOR_ACTIONS.ADD_COMMENT,
    EDITOR_ACTIONS.MARK_SECTION_COMPLETE,
    EDITOR_ACTIONS.MARK_DOCUMENT_COMPLETE,
    EDITOR_ACTIONS.REQUEST_REVIEW,
    EDITOR_ACTIONS.APPROVE,
    EDITOR_ACTIONS.REJECT,
    EDITOR_ACTIONS.VIEW_HISTORY,
    EDITOR_ACTIONS.DOWNLOAD,
  ],
  
  [USER_ROLES.USER]: [
    EDITOR_ACTIONS.EDIT_TEXT,
    EDITOR_ACTIONS.SUGGEST_CHANGES,
    EDITOR_ACTIONS.ADD_COMMENT,
    EDITOR_ACTIONS.MARK_SECTION_COMPLETE,
    EDITOR_ACTIONS.MARK_DOCUMENT_COMPLETE,
    EDITOR_ACTIONS.REQUEST_REVIEW,
    EDITOR_ACTIONS.VIEW_HISTORY,
    EDITOR_ACTIONS.DOWNLOAD,
  ],
  
  [USER_ROLES.REVIEWER]: [
    EDITOR_ACTIONS.SUGGEST_CHANGES,
    EDITOR_ACTIONS.ADD_COMMENT,
    EDITOR_ACTIONS.APPROVE,
    EDITOR_ACTIONS.REJECT,
    EDITOR_ACTIONS.VIEW_HISTORY,
    EDITOR_ACTIONS.DOWNLOAD,
  ],
};

// ============================================
// PERMISSION CHECK FUNCTIONS
// ============================================

/**
 * Check if a role has a specific permission
 * @param {string} role - User role
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (role, permission) => {
  if (!role || !permission) return false;
  const rolePermissions = ROLE_PERMISSIONS[role] || [];
  return rolePermissions.includes(permission);
};

/**
 * Check if a role has all specified permissions
 * @param {string} role - User role
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (role, permissions) => {
  if (!role || !permissions || !permissions.length) return false;
  return permissions.every(permission => hasPermission(role, permission));
};

/**
 * Check if a role has any of the specified permissions
 * @param {string} role - User role
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (role, permissions) => {
  if (!role || !permissions || !permissions.length) return false;
  return permissions.some(permission => hasPermission(role, permission));
};

/**
 * Get all permissions for a role
 * @param {string} role - User role
 * @returns {string[]}
 */
export const getPermissionsForRole = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

/**
 * Check if a role can perform an editor action
 * @param {string} role - User role
 * @param {string} action - Editor action
 * @returns {boolean}
 */
export const canPerformEditorAction = (role, action) => {
  if (!role || !action) return false;
  const roleActions = EDITOR_ACTION_PERMISSIONS[role] || [];
  return roleActions.includes(action);
};

/**
 * Get available editor actions for a role
 * @param {string} role - User role
 * @returns {string[]}
 */
export const getEditorActionsForRole = (role) => {
  return EDITOR_ACTION_PERMISSIONS[role] || [];
};

/**
 * Check if user can access a document based on role and document ownership
 * @param {object} user - User object
 * @param {object} document - Document object
 * @returns {boolean}
 */
export const canAccessDocument = (user, document) => {
  if (!user || !document) return false;
  
  // Admin can access all documents
  if (user.role === USER_ROLES.ADMIN) return true;
  
  // User can access own documents
  if (user.role === USER_ROLES.USER && document.createdBy === user.id) return true;
  
  // Reviewer can access assigned documents
  if (user.role === USER_ROLES.REVIEWER && document.assignedReviewer === user.id) return true;
  
  return false;
};

/**
 * Check if user can edit a document
 * @param {object} user - User object
 * @param {object} document - Document object
 * @returns {boolean}
 */
export const canEditDocument = (user, document) => {
  if (!user || !document) return false;
  
  // Admin can edit all documents
  if (user.role === USER_ROLES.ADMIN) return true;
  
  // User can edit own documents if not approved/rejected
  if (user.role === USER_ROLES.USER && document.createdBy === user.id) {
    return !['APPROVED', 'REJECTED'].includes(document.status);
  }
  
  // Reviewers cannot edit documents (only comment/approve/reject)
  return false;
};

/**
 * Check if user can delete a document
 * @param {object} user - User object
 * @param {object} document - Document object
 * @returns {boolean}
 */
export const canDeleteDocument = (user, document) => {
  if (!user || !document) return false;
  
  // Only admin can delete documents
  return user.role === USER_ROLES.ADMIN;
};

/**
 * Check if user can approve/reject a document
 * @param {object} user - User object
 * @param {object} document - Document object
 * @returns {boolean}
 */
export const canReviewDocument = (user, document) => {
  if (!user || !document) return false;
  
  // Admin can review all documents
  if (user.role === USER_ROLES.ADMIN) return true;
  
  // Reviewer can review assigned documents in review state
  if (user.role === USER_ROLES.REVIEWER && document.assignedReviewer === user.id) {
    return ['PENDING_REVIEW', 'IN_REVIEW'].includes(document.status);
  }
  
  return false;
};

// ============================================
// ROUTE ACCESS CONTROL
// ============================================

export const ROUTE_PERMISSIONS = {
  '/admin/dashboard': [USER_ROLES.ADMIN],
  '/admin/templates': [USER_ROLES.ADMIN],
  '/admin/users': [USER_ROLES.ADMIN],
  '/admin/prompts': [USER_ROLES.ADMIN],
  '/config': [USER_ROLES.ADMIN],
  '/user/dashboard': [USER_ROLES.USER],
  '/reviewer/dashboard': [USER_ROLES.REVIEWER],
  '/documents': [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.REVIEWER],
  '/documents/create': [USER_ROLES.ADMIN, USER_ROLES.USER],
  '/settings': [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.REVIEWER],
};

/**
 * Check if a role can access a route
 * @param {string} role - User role
 * @param {string} path - Route path
 * @returns {boolean}
 */
export const canAccessRoute = (role, path) => {
  if (!role || !path) return false;
  
  // Admin can access all routes
  if (role === USER_ROLES.ADMIN) return true;
  
  // Check exact match first
  if (ROUTE_PERMISSIONS[path]) {
    return ROUTE_PERMISSIONS[path].includes(role);
  }
  
  // Check for dynamic routes (e.g., /documents/:id/editor)
  if (path.startsWith('/documents/') && path.endsWith('/editor')) {
    return [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.REVIEWER].includes(role);
  }
  
  // Default deny for unknown routes
  return false;
};
